import type { SuggestedDesk } from "@/lib/types/desk-suggestions";

export interface ExistingDesk {
  id: string;
  name: string;
  description?: string | null;
  template_id?: string | null;
  /** service-desk matches disabled desks too; false when the desk is disabled. */
  enabled?: boolean;
}

/** Mirrors service-desk's name slug: lowercase, drop punctuation, dash-join words. */
export function slugifyDeskName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\p{L}\p{N}_\s-]/gu, "")
    .replace(/[-\s]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The id service-desk gives a custom desk named `name`. */
export function customDeskId(name: string): string {
  return `custom-${slugifyDeskName(name).slice(0, 53)}`;
}

/** Name equality as service-desk's provisioning compares names. */
export function sameName(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
}

/**
 * The tenant desk a suggestion already exists as: the same id or name
 * (service-desk's provisioning rules), or a desk built from the same template.
 */
export function findExistingDesk<T extends ExistingDesk>(
  suggestion: SuggestedDesk,
  desks: T[],
): T | undefined {
  const expectedId = suggestion.template_id ?? customDeskId(suggestion.name);
  return (
    desks.find(
      (desk) =>
        desk.id === expectedId ||
        (suggestion.template_id !== null &&
          desk.template_id === suggestion.template_id),
    ) ?? desks.find((desk) => sameName(desk.name, suggestion.name))
  );
}

export interface ExistingSubdesk {
  id: string;
  name: string;
}

/** The id service-desk gives a subdesk named `name` within its desk. */
export function subdeskIdForName(name: string): string {
  return slugifyDeskName(name).slice(0, 60);
}

/**
 * The desk's subdesk a suggested subdesk already exists as: the same id or
 * name, as service-desk's provisioning matches them.
 */
export function findExistingSubdesk<T extends ExistingSubdesk>(
  name: string,
  subdesks: T[],
): T | undefined {
  const expectedId = subdeskIdForName(name);
  return (
    subdesks.find((subdesk) => subdesk.id === expectedId) ??
    subdesks.find((subdesk) => sameName(subdesk.name, name))
  );
}
